/**
 * Lot-timezone time formatting — the single source of every wall-clock string
 * the driver sees.
 *
 * A stay is priced and enforced on the lot's clock, so times must render in
 * the lot's IANA timezone whatever the device is set to: '8:25 PM CT' in
 * English, '芝加哥时间 20:25' in Chinese. Zone names are derived from Intl for
 * whichever timezone the lot declares — nothing in this module knows about
 * Chicago specifically, and no server display string is ever rendered.
 */

export type LotTimeLanguage = 'en' | 'zh';

export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** English is 12-hour, Chinese 24-hour; a single flow never mixes the two. */
const LOCALES: Record<LotTimeLanguage, string> = { en: 'en-US', zh: 'zh-CN' };

/** i18next reports region tags (zh-CN, en-GB); only the zh/en split matters here. */
export function resolveLotTimeLanguage(language: string): LotTimeLanguage {
  return language.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

/**
 * Stand-in when a response carries no lot timezone: the device zone, labelled
 * as itself. The label must stay honest — a device-zone time is never allowed
 * to wear a lot's zone name, and no lot zone is ever hardcoded as data.
 */
export function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(
  locale: string,
  options: Intl.DateTimeFormatOptions & { timeZone: string },
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = formatters.get(key);
  if (cached) return cached;
  const next = new Intl.DateTimeFormat(locale, options);
  formatters.set(key, next);
  return next;
}

const toDate = (instant: Date | string): Date => (typeof instant === 'string' ? new Date(instant) : instant);

/** en-US separates AM/PM with U+202F; UI strings need an ordinary space. */
const plainSpaces = (text: string): string => text.replace(/[\u202f\u00a0]/g, ' ');

/**
 * The zone name alone: 'CT' (en) / '芝加哥时间' (zh). `shortGeneric` is
 * DST-stable — the label cannot flip CDT/CST mid-flow across a transition.
 * Engines lacking that CLDR data fall back to the offset-bearing short name;
 * the raw IANA id is the last resort, never an empty label.
 */
export function lotZoneLabel(
  timezone: string | undefined,
  language: LotTimeLanguage,
  at: Date = new Date(),
): string {
  const zone = timezone ?? deviceTimezone();
  const locale = LOCALES[language];
  for (const timeZoneName of ['shortGeneric', 'short'] as const) {
    const name = formatter(locale, { timeZone: zone, timeZoneName, hour: 'numeric' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
    if (name) return plainSpaces(name);
  }
  return zone;
}

/** Bare clock in the lot's zone: '8:25 PM' (en, 12h) / '20:25' (zh, 24h). */
export function lotClock(
  instant: Date | string,
  timezone: string | undefined,
  language: LotTimeLanguage,
): string {
  const zone = timezone ?? deviceTimezone();
  const options: Intl.DateTimeFormatOptions & { timeZone: string } =
    language === 'zh'
      ? { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
      : { timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true };
  return plainSpaces(formatter(LOCALES[language], options).format(toDate(instant)));
}

/** Clock plus zone: '8:25 PM CT' / '芝加哥时间 20:25'. Chinese reads zone-first. */
export function lotClockLabel(
  instant: Date | string,
  timezone: string | undefined,
  language: LotTimeLanguage,
): string {
  const d = toDate(instant);
  const clock = lotClock(d, timezone, language);
  const zone = lotZoneLabel(timezone, language, d);
  return language === 'zh' ? `${zone} ${clock}` : `${clock} ${zone}`;
}

/** A start–end span with the zone named once: '8:25 AM – 10:25 PM CT'. */
export function lotSpanLabel(
  start: Date | string,
  end: Date | string,
  timezone: string | undefined,
  language: LotTimeLanguage,
): string {
  const endDate = toDate(end);
  const span = `${lotClock(start, timezone, language)} – ${lotClock(endDate, timezone, language)}`;
  const zone = lotZoneLabel(timezone, language, endDate);
  return language === 'zh' ? `${zone} ${span}` : `${span} ${zone}`;
}

interface CalendarDay {
  year: number;
  month: number;
  day: number;
}

function lotCalendarDay(instant: Date, zone: string): CalendarDay {
  const parts = formatter('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(instant);
  const read = (type: 'year' | 'month' | 'day') =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: read('year'), month: read('month'), day: read('day') };
}

const sameCalendarDay = (a: CalendarDay, b: CalendarDay): boolean =>
  a.year === b.year && a.month === b.month && a.day === b.day;

/** Calendar increment, deliberately not +24h: DST days are 23 or 25 hours long. */
function nextCalendarDay(day: CalendarDay): CalendarDay {
  const utc = new Date(Date.UTC(day.year, day.month - 1, day.day + 1));
  return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate() };
}

/** Same calendar date on the LOT's clock — two instants on one UTC date can differ. */
export function isSameLotDay(a: Date | string, b: Date | string, timezone: string | undefined): boolean {
  const zone = timezone ?? deviceTimezone();
  return sameCalendarDay(lotCalendarDay(toDate(a), zone), lotCalendarDay(toDate(b), zone));
}

/**
 * 'YYYY-MM-DD' in the lot's calendar — directly comparable to an
 * `<input type="date">` value, so date filtering follows the lot's day
 * boundaries, not the device's.
 */
export function lotISODate(instant: Date | string, timezone: string | undefined): string {
  const day = lotCalendarDay(toDate(instant), timezone ?? deviceTimezone());
  return `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
}

/** 'M/D' in the lot's calendar, for rows where the day matters more than the clock. */
export function lotDateLabel(instant: Date | string, timezone: string | undefined): string {
  const day = lotCalendarDay(toDate(instant), timezone ?? deviceTimezone());
  return `${day.month}/${day.day}`;
}

/**
 * The concrete expiry moment with today/tomorrow/date resolved against the
 * LOT's calendar, not the device's — midnight is the lot's midnight, so a stay
 * crossing it says "tomorrow" even when the device's date hasn't turned yet.
 */
export function lotExpiryLabel(
  instant: Date | string,
  timezone: string | undefined,
  language: LotTimeLanguage,
  t: Translate,
  now: Date = new Date(),
): string {
  const zone = timezone ?? deviceTimezone();
  const d = toDate(instant);
  const time = lotClockLabel(d, zone, language);
  const target = lotCalendarDay(d, zone);
  const today = lotCalendarDay(now, zone);
  if (sameCalendarDay(target, today)) return t('units.todayAt', { time });
  if (sameCalendarDay(target, nextCalendarDay(today))) return t('units.tomorrowAt', { time });
  return t('units.dateAt', { date: `${target.month}/${target.day}`, time });
}
